import { Icon } from '@assembly-js/design-system';
import { Divider } from '@mui/material';
import cx from 'classnames';
import React, { ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';

import MenuLogo from 'src/features/drawer/MenuLogo';
import { NonHoverBorder } from 'src/theme/colors';

import { Action, BaseActionsMenu } from '../Dropdowns';
import BaseTypography from '../Text/BaseTypography';

interface Props {
  title: ReactNode;
  actions?: Action[];
  actionMenuAnchorSetter?: (anchorEl: HTMLElement | null) => void;
  onBackClick?: () => void;
  AdditionalSlotsComponents?: React.ReactNode[];
  isChannelsListShown?: boolean;
}

const useStyles = makeStyles<Pick<Props, 'isChannelsListShown'>>()(
  (theme, props) => ({
    root: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: '60px',
      width: '-webkit-fill-available',
      borderBottom: `1px solid ${NonHoverBorder}`,
      boxSizing: 'border-box',
      // on mobile screens
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 2.5),
      },
      // desktop screens
      [theme.breakpoints.up('md')]: {
        padding: props.isChannelsListShown
          ? theme.spacing(0, 2.5)
          : theme.spacing(0, 4.5),
      },
    },
    titleSlot: {
      gap: theme.spacing(1.5),
    },
    titleText: {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      maxWidth: '100%',
      marginRight: theme.spacing(2.5),
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
    },
    actionsGap: {
      gap: theme.spacing(1.5),
    },
    actionsShrink: {
      flexShrink: 0,
    },
  }),
);

// ref is used to get the width of the toolbar container.
// This is used to calculate the width of the search input when expanded.
export const ChannelContentToolbar = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      actions,
      title,
      actionMenuAnchorSetter,
      onBackClick: handleBackClick,
      AdditionalSlotsComponents = [],
      isChannelsListShown = true,
    },
    ref,
  ) => {
    const { classes } = useStyles({ isChannelsListShown });

    return (
      <div className={classes.root} ref={ref}>
        <div
          className={cx(classes.actions, {
            [classes.actionsGap]: title,
          })}
        >
          {handleBackClick ? (
            <MenuLogo
              menuClickCallback={handleBackClick}
              Icon={<Icon icon="ChevronLeft" />}
            />
          ) : (
            <MenuLogo />
          )}
          <BaseTypography className={classes.titleText} fontType="13Medium">
            {title}
          </BaseTypography>
        </div>
        <div
          className={cx(classes.actions, classes.actionsShrink, {
            [classes.actionsGap]: actions && actions.length > 0,
          })}
        >
          {actions && actions.length > 0 && (
            <BaseActionsMenu
              contained
              actions={actions}
              onActionButtonAnchorElSet={actionMenuAnchorSetter}
            />
          )}
          {AdditionalSlotsComponents.length > 0 &&
            actions &&
            actions.length > 0 && <Divider orientation="vertical" flexItem />}
          {AdditionalSlotsComponents.map((ComponentNode, index) => (
            <React.Fragment key={`additional-toolbar-slot${index}`}>
              {ComponentNode}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  },
);
